'use strict';

const libPictProvider = require('pict-provider');
const libProjectEditAccess = require('../access/Project-Edit-Access.js');

const BODY_MOUNT = '#PEW-Body';
const MESSAGE_MOUNT = '#PEW-Message';
// CustomProperties lives in its OWN mount (a sibling of #PEW-Body), so repainting the body never tears down
// the editable object-editor tree mounted inside it. The tree edits AppData.ProjectEditWorkspace.CustomPropsData
// in place; the view owns the mount (see ProjectEditWorkspace.mountCustomProps).
const CUSTOMPROPS_MOUNT = '#PEW-CustomProps';

// The Financial/Schedule fields locked once a Project is Active. Editable before Active; read-only (and
// omitted from the save payload) once Status is 'Active'. Mirrors the Project-Edit.json "Schedule &
// Financials" section — the only editable Schedule/Financials fields (the rest are computed/read-only).
const GATED_FIELDS = [ 'InitialContractAmount', 'CostPerDay', 'OriginalContractedDays', 'ContractedDays' ];
const GATED_GROUP = 'Schedule & Financials';
const ACTIVE_STATUS = 'Active';

// Editable Project fields, grouped for the form (parity with default_manifests/Project-Edit.json). Order +
// grouping drive the render; Type picks the control. Gated fields lock when the loaded project is Active.
const FIELD_GROUPS =
[
	{ Name: 'Identity', Fields:
		[
			{ Key: 'Name', Label: 'Project Name', Type: 'text', Wide: true, Required: true },
			{ Key: 'Status', Label: 'Status', Type: 'text' },
			{ Key: 'ExternalProjectID', Label: 'External Project ID', Type: 'text' },
		] },
	{ Name: 'Relationships', Fields:
		[
			// Full-width + stacked: entity labels (contractor names, contract descriptions) are long, and a
			// half-width picker truncates both the search rows and the selected value to unreadable.
			{ Key: 'IDProjectType', Label: 'Project Type', Type: 'entity', Entity: 'ProjectType', Wide: true },
			{ Key: 'IDOrganization', Label: 'Prime Contractor', Type: 'entity', Entity: 'Organization', Wide: true },
			{ Key: 'IDContract', Label: 'Contract', Type: 'entity', Entity: 'Contract', Wide: true },
		] },
	{ Name: GATED_GROUP, Fields:
		[
			{ Key: 'InitialContractAmount', Label: 'Initial Contract Amount', Type: 'number', Gated: true },
			{ Key: 'CostPerDay', Label: 'Cost / Day', Type: 'number', Gated: true },
			{ Key: 'OriginalContractedDays', Label: 'Original Contracted Days', Type: 'number', Gated: true },
			{ Key: 'ContractedDays', Label: 'Contracted Days', Type: 'number', Gated: true },
		] },
	{ Name: 'Description', Fields:
		[
			{ Key: 'Description', Label: 'Description', Type: 'textarea', Wide: true },
		] },
];

// Server-side entity-picker config per relationship field (SearchFields mirror Project-Edit.json). The picker
// searches the whole set on the server (this app has no pict.EntityProvider, so the DataProvider/ResolveValue
// go through HeadlightRestClient) and shows the first PICKER_PAGE_SIZE, paging in more on scroll.
const PICKER_PAGE_SIZE = 25;
const ENTITY_CONFIG =
{
	IDOrganization: { Set: 'Organizations', One: 'Organization', IDField: 'IDOrganization', Noun: 'prime contractor', SearchFields: [ 'Name', 'City', 'State', 'VendorCode', 'Type' ], IgnoreFields: [ 'IDCustomer', 'GUIDOrganization' ] },
	IDContract: { Set: 'Contracts', One: 'Contract', IDField: 'IDContract', Noun: 'contract', SearchFields: [ 'Name', 'Description', 'PrimaryProjectNumber', 'ExternalContractID', 'Status' ], IgnoreFields: [ 'IDCustomer', 'GUIDContract' ] },
	// ProjectType is customer-scoped but has NO Deleted column (see Headlight.json) — NoSoftDelete drops the
	// `Deleted~EQ~0` clause the picker otherwise appends, which would otherwise make the ProjectTypes query fail.
	IDProjectType: { Set: 'ProjectTypes', One: 'ProjectType', IDField: 'IDProjectType', Noun: 'project type', SearchFields: [ 'Name', 'Description' ], IgnoreFields: [ 'IDCustomer', 'GUIDProjectType' ], NoSoftDelete: true },
};

/**
 * ProjectEditWorkspace — the data layer behind the bespoke Project EDIT screen. Reached by the app-router
 * Edit bridge (#/PSRS/Project/Edit/<GUID>) that shadows the generic recordset edit form; owns its own
 * render / save / validation. The reason it exists: the Financial & Schedule fields must be read-only once
 * a project is Active (editable before), and the manifest-driven recordset form has no per-record
 * conditional read-only. Full field parity with Project-Edit.json so it cleanly replaces that form. Someone the
 * server would refuse a save (canEdit) gets the same screen read-only, with no Save.
 *
 * State (a working copy of the record's editable fields) lives in pict.AppData.ProjectEditWorkspace; the
 * thin view re-derives its HTML from it each paint (Entitlements pattern — hand-built HTML into mounts).
 * Saving is a Meadow partial PUT (like ProjectWorkspace-Provider.saveCenterAsync).
 */
class ProjectEditWorkspaceProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;

		// The loaded record (source of truth for gating + reset); the working copy lives in state.Fields.
		this._project = null;
	}

	get restClient()
	{
		return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
	}

	get state()
	{
		if (!this.pict.AppData.ProjectEditWorkspace)
		{
			this.pict.AppData.ProjectEditWorkspace =
			{
				Loading: false, Loaded: false, Message: '', MessageClass: '', Saving: false,
				IDProject: 0, GUIDProject: '', IDCustomer: 0, Title: 'Edit Project',
				Status: '', IsActive: false,
				Fields: {}, Errors: {}, CustomPropsData: {},
			};
		}
		return this.pict.AppData.ProjectEditWorkspace;
	}

	/** The loaded project is Active — Financial/Schedule fields lock. Gate keys off the persisted Status. */
	get isActive()
	{
		return this.state.IsActive === true;
	}

	/**
	 * May the current user save the loaded project? The server's rule (admin, role 4 in the project's customer, or the
	 * project's creator; see access/Project-Edit-Access.js). Without it the screen opens read-only, with no Save.
	 * @return {boolean}
	 */
	get canEdit()
	{
		const tmpUser = (this.pict.AppData && this.pict.AppData.CurrentUser) || {};
		return libProjectEditAccess.canEditProject(tmpUser, this._project);
	}

	// --- REST helpers (mirrors ProjectWorkspace-Provider) ---

	_getJSON(pURL)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.getJSON(pURL, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	_putJSON(pURL, pPayload)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.putJSON(pURL, pPayload, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	_esc(pValue)
	{
		return String(pValue == null ? '' : pValue).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar]));
	}

	_toNumberOrNull(pValue)
	{
		if (pValue == null || String(pValue).trim() === '') { return null; }
		const tmpNumber = Number(pValue);
		return Number.isFinite(tmpNumber) ? tmpNumber : null;
	}

	// --- Loading ---

	/** Open by GUID (the recordset list + the read workspace link by GUID); resolve to the row id, then load. */
	async openByGUIDAsync(pGUID)
	{
		const tmpState = this.state;
		tmpState.Loading = true;
		tmpState.Loaded = false;
		tmpState.Message = '';
		this.render();
		try
		{
			const tmpRows = await this._getJSON(`Projects/FilteredTo/FBV~GUIDProject~EQ~${pGUID}`);
			const tmpRecord = (Array.isArray(tmpRows) && tmpRows.length > 0) ? tmpRows[0] : null;
			if (!tmpRecord) { throw new Error(`No project found for GUID ${pGUID}.`); }
			await this.loadAsync(tmpRecord.IDProject);
		}
		catch (pError)
		{
			tmpState.Loading = false;
			tmpState.Message = `Could not load the project: ${pError.message || pError}`;
			tmpState.MessageClass = 'is-danger';
			this.render();
		}
	}

	/** Load a project into the edit workspace by id and seed the working copy. */
	async loadAsync(pIDProject)
	{
		const tmpState = this.state;
		tmpState.Loading = true;
		tmpState.Message = '';
		this.render();
		try
		{
			const tmpProject = await this._getJSON(`Project/${pIDProject}`);
			if (!tmpProject || !tmpProject.IDProject) { throw new Error(`Project ${pIDProject} not found.`); }
			this._project = tmpProject;
			tmpState.IDProject = tmpProject.IDProject;
			tmpState.GUIDProject = tmpProject.GUIDProject || '';
			tmpState.IDCustomer = Number(tmpProject.IDCustomer) || 0;
			tmpState.Status = tmpProject.Status || '';
			tmpState.IsActive = (String(tmpProject.Status) === ACTIVE_STATUS);
			tmpState.Title = tmpProject.Name || `Project ${tmpProject.IDProject}`;
			tmpState.Errors = {};
			// Seed the working copy from the record (one value per editable field). A set entity FK is kept as
			// a number so the picker (ValueAddress-bound) preselects the matching option; an UNSET FK is kept
			// as '' (not 0) so the picker shows its placeholder instead of a literal "0". On save, '' round-
			// trips back to null through _toNumberOrNull, clearing the FK.
			const tmpFields = {};
			FIELD_GROUPS.forEach((pGroup) => pGroup.Fields.forEach((pField) =>
			{
				const tmpRaw = tmpProject[pField.Key];
				if (pField.Type === 'entity') { tmpFields[pField.Key] = Number(tmpRaw) || ''; return; }
				tmpFields[pField.Key] = (tmpRaw == null) ? '' : tmpRaw;
			}));
			tmpState.Fields = tmpFields;
			// CustomProperties → a live object the editable object-editor tree binds to + mutates in place.
			tmpState.CustomPropsData = this._parseCustomProperties(tmpProject.CustomProperties);
			tmpState.Loading = false;
			tmpState.Loaded = true;
			this.render();
			this.renderCustomProps();
		}
		catch (pError)
		{
			tmpState.Loading = false;
			tmpState.Message = `Could not load the project: ${pError.message || pError}`;
			tmpState.MessageClass = 'is-danger';
			this.render();
		}
	}

	/** CustomProperties arrives as a JSON string or an already-parsed object; normalise to a plain object. */
	_parseCustomProperties(pValue)
	{
		if (pValue == null || pValue === '') { return {}; }
		if (typeof pValue === 'object') { return pValue; }
		try { const tmpParsed = JSON.parse(pValue); return (tmpParsed && typeof tmpParsed === 'object') ? tmpParsed : {}; }
		catch (pError) { return {}; }
	}

	// --- Editing ---

	/** Update one working-copy field. onchange-driven, so no re-render (the DOM already holds the value). */
	setField(pKey, pValue)
	{
		this.state.Fields[pKey] = pValue;
		if (this.state.Errors[pKey]) { delete this.state.Errors[pKey]; }
	}

	_validate()
	{
		const tmpState = this.state;
		const tmpErrors = {};
		FIELD_GROUPS.forEach((pGroup) => pGroup.Fields.forEach((pField) =>
		{
			if (pField.Gated && this.isActive) { return; }   // locked fields aren't validated/saved
			const tmpValue = tmpState.Fields[pField.Key];
			const tmpHasValue = (tmpValue != null && String(tmpValue).trim() !== '');
			if (pField.Required && !tmpHasValue) { tmpErrors[pField.Key] = `${pField.Label} is required.`; return; }
			if (pField.Type === 'number' && tmpHasValue)
			{
				const tmpNumber = Number(tmpValue);
				if (!Number.isFinite(tmpNumber)) { tmpErrors[pField.Key] = `${pField.Label} must be a number.`; }
				else if (tmpNumber < 0) { tmpErrors[pField.Key] = `${pField.Label} can’t be negative.`; }
			}
		}));
		tmpState.Errors = tmpErrors;
		return Object.keys(tmpErrors).length === 0;
	}

	// --- Save / navigation ---

	async saveAsync()
	{
		const tmpState = this.state;
		if (tmpState.Saving || !this._project) { return; }
		if (!this.canEdit)
		{
			return;
		}
		if (!this._validate())
		{
			tmpState.Message = 'Please fix the highlighted fields.';
			tmpState.MessageClass = 'is-danger';
			this.render();
			return;
		}
		tmpState.Saving = true;
		tmpState.Message = 'Saving…';
		tmpState.MessageClass = 'is-info';
		this._renderMessage();
		try
		{
			const tmpPayload = { IDProject: this._project.IDProject };
			FIELD_GROUPS.forEach((pGroup) => pGroup.Fields.forEach((pField) =>
			{
				// Financial/Schedule fields are omitted entirely while Active — the persisted values stand.
				if (pField.Gated && this.isActive) { return; }
				const tmpValue = tmpState.Fields[pField.Key];
				if ((pField.Type === 'number') || (pField.Type === 'entity'))
				{
					tmpPayload[pField.Key] = this._toNumberOrNull(tmpValue);
				}
				else
				{
					tmpPayload[pField.Key] = (tmpValue == null) ? '' : tmpValue;
				}
			}));
			// Flush any pending guided metadata-form edit into CustomPropsData, then serialize the live bound object
			// (the guided form + the raw object editor both mutate it in place).
			const tmpMetaForm = this.pict.providers.EntityMetadataForm;
			if (tmpMetaForm && (typeof tmpMetaForm.flush === 'function')) { tmpMetaForm.flush(); }
			tmpPayload.CustomProperties = JSON.stringify(tmpState.CustomPropsData || {});
			const tmpSaved = await this._putJSON('Project', tmpPayload);
			if (tmpSaved && tmpSaved.ErrorCode)
			{
				throw new Error('the server rejected the update (you may not have permission to edit this project).');
			}
			tmpState.Saving = false;
			this._navigateToView();
		}
		catch (pError)
		{
			tmpState.Saving = false;
			tmpState.Message = `Could not save the project: ${pError.message || pError}`;
			tmpState.MessageClass = 'is-danger';
			this.render();
		}
	}

	cancel()
	{
		this._navigateToView();
	}

	_navigateToView()
	{
		if ((typeof window !== 'undefined') && window.location)
		{
			const tmpState = this.state;
			window.location.hash = tmpState.GUIDProject ? `#/PSRS/Project/View/${tmpState.GUIDProject}` : '#/PSRS/Project/List';
		}
	}

	// --- Render (hand-built HTML into the shell's mounts) ---

	/** Paint the whole edit body into #PEW-Body. Called on load / save error (not on every keystroke). */
	render()
	{
		if (this.pict.ContentAssignment.getElement(BODY_MOUNT).length === 0) { return; }
		this.pict.ContentAssignment.assignContent(BODY_MOUNT, this._bodyHTML());
		if (this.state.Loaded) { this._mountPickers(); }
	}

	/**
	 * (Re)mount the searchable entity pickers into their hosts inside #PEW-Body. createPicker reconfigures +
	 * reuses the same view, and each picker is ValueAddress-bound to its Fields entry, so a body repaint
	 * re-renders it with the current selection intact. Server-side search (DataProvider) + preselect-resolve
	 * (ResolveValue) go through HeadlightRestClient — this app has no pict.EntityProvider.
	 */
	_mountPickers()
	{
		const tmpPickerProvider = this.pict.providers['Pict-Section-Picker'];
		if (!tmpPickerProvider || (typeof tmpPickerProvider.createPicker !== 'function')) { return; }
		FIELD_GROUPS.forEach((pGroup) => pGroup.Fields.forEach((pField) =>
		{
			if (pField.Type === 'entity') { this._mountPicker(pField.Key); }
		}));
	}

	_mountPicker(pKey)
	{
		const tmpMount = `#PEW-Picker-${pKey}`;
		if (this.pict.ContentAssignment.getElement(tmpMount).length === 0) { return; }
		const tmpConfig = ENTITY_CONFIG[pKey];
		if (!tmpConfig) { return; }
		const tmpPicker = this.pict.providers['Pict-Section-Picker'].createPicker(`PEW-Picker-${pKey}`,
			{
				DestinationAddress: tmpMount,
				ValueAddress: `AppData.ProjectEditWorkspace.Fields.${pKey}`,
				Searchable: true,
				AllowClear: true,
				ReadOnly: !this.canEdit,
				ClearLabel: '(none)',
				Placeholder: this.canEdit ? `Search a ${tmpConfig.Noun}…` : '—',
				DataProvider: this._entityDataProvider(tmpConfig),
				ResolveValue: this._entityResolveValue(tmpConfig),
				// This is a custom picker (it bypasses recordset), so it opts into field decoration directly:
				// the ⚙ lets the user pin an extra column (e.g. VendorCode) onto each row to tell same-named
				// orgs apart. Entity keys the per-user localStorage choice; the rows carry a Record (see
				// _entityDataProvider) for the field values.
				Entity: tmpConfig.One,
				AllowFieldDecoration: true,
				DecorationIgnoreFields: tmpConfig.IgnoreFields,
			});
		if (tmpPicker && (typeof tmpPicker.render === 'function')) { tmpPicker.render(); }
	}

	/**
	 * A server-side search DataProvider — `(searchTerm, page) => Promise<{results:[{Value,Text}], hasMore}>` —
	 * scoped to the project's customer, paging PICKER_PAGE_SIZE at a time. Reuses the picker's own
	 * buildSearchFilter for the OR-group LIKE stanza so the FoxHound syntax matches the framework exactly.
	 */
	_entityDataProvider(pConfig)
	{
		const tmpPickerProvider = this.pict.providers['Pict-Section-Picker'];
		return (pSearchTerm, pPage) =>
		{
			const tmpStart = (Number(pPage) || 0) * PICKER_PAGE_SIZE;
			// Most entities soft-delete; a few (e.g. ProjectType) have no Deleted column, so skip the clause for them.
			let tmpFilter = `FBV~IDCustomer~EQ~${this.state.IDCustomer}`;
			if (!pConfig.NoSoftDelete) { tmpFilter += `~FBV~Deleted~EQ~0`; }
			const tmpTerm = String(pSearchTerm == null ? '' : pSearchTerm).trim();
			if (tmpTerm && tmpPickerProvider && (typeof tmpPickerProvider.buildSearchFilter === 'function'))
			{
				tmpFilter += `~${tmpPickerProvider.buildSearchFilter(pConfig.SearchFields, tmpTerm)}`;
			}
			return this._getJSON(`${pConfig.Set}/FilteredTo/${tmpFilter}/${tmpStart}/${PICKER_PAGE_SIZE}`)
				.then((pRows) =>
				{
					const tmpList = Array.isArray(pRows) ? pRows : [];
					// Record carries the full row so the picker's field-decoration chooser can offer + show its columns.
					return { results: tmpList.map((pRow) => ({ Value: Number(pRow[pConfig.IDField]), Text: pRow.Name || `#${pRow[pConfig.IDField]}`, Record: pRow })), hasMore: (tmpList.length >= PICKER_PAGE_SIZE) };
				})
				.catch(() => ({ results: [], hasMore: false }));
		};
	}

	/** Resolve a pre-bound FK id → {Value,Text} so the current selection shows its name on first render. */
	_entityResolveValue(pConfig)
	{
		return (pValue) =>
		{
			const tmpID = Number(pValue);
			if (!tmpID) { return Promise.resolve(null); }
			return this._getJSON(`${pConfig.One}/${tmpID}`)
				.then((pRow) => ((pRow && pRow[pConfig.IDField]) ? { Value: Number(pRow[pConfig.IDField]), Text: pRow.Name || `#${tmpID}` } : { Value: tmpID, Text: `#${tmpID}` }))
				.catch(() => ({ Value: tmpID, Text: `#${tmpID}` }));
		};
	}

	/**
	 * Paint the Custom Properties section into its OWN mount and mount the editable object-editor tree — kept
	 * separate from #PEW-Body so a body repaint (save error / validation) never tears the tree down. Only runs
	 * once loaded; the tree binds to (and mutates in place) AppData.ProjectEditWorkspace.CustomPropsData.
	 */
	renderCustomProps()
	{
		if (!this.state.Loaded) { return; }
		if (this.pict.ContentAssignment.getElement(CUSTOMPROPS_MOUNT).length === 0) { return; }
		this.pict.ContentAssignment.assignContent(CUSTOMPROPS_MOUNT, this._customPropsSectionHTML());
		// Guided per-customer metadata form (editable), with an entitlement-gated raw-JSON advanced toggle. Binds to
		// the same CustomPropsData object the save serializes (:290), so form edits + any advanced raw edits persist.
		const tmpProvider = this.pict.providers.EntityMetadataForm;
		if (tmpProvider && (typeof tmpProvider.openInto === 'function'))
		{
			tmpProvider.openInto({ entityType: 'Project', idCustomer: Number(this._project && this._project.IDCustomer) || 0, dataAddress: 'AppData.ProjectEditWorkspace.CustomPropsData', host: '#PEW-CustomPropsHost', mode: this.canEdit ? 'edit' : 'view' });
			return;
		}
		// Fallback: the bare editable object-editor tree.
		const tmpView = this.pict.views.ProjectEditWorkspace;
		if (tmpView && (typeof tmpView.mountCustomProps === 'function')) { tmpView.mountCustomProps(); }
	}

	_customPropsSectionHTML()
	{
		return `<div class="pew-form pew-customprops-panel"><div class="pew-group pew-group-customprops">`
			+ `<div class="pew-group-head">Custom Properties</div>`
			+ `<div id="PEW-CustomPropsHost" class="pew-objecteditor-host"></div>`
			+ `</div></div>`;
	}

	/** Repaint just the message region (save status) so the form isn't rebuilt mid-save. */
	_renderMessage()
	{
		if (this.pict.ContentAssignment.getElement(MESSAGE_MOUNT).length === 0) { this.render(); return; }
		this.pict.ContentAssignment.assignContent(MESSAGE_MOUNT, this._messageHTML());
	}

	_messageHTML()
	{
		const tmpState = this.state;
		if (!tmpState.Message) { return ''; }
		return `<div class="notification ${this._esc(tmpState.MessageClass || 'is-info')} is-soft py-2 px-3 mb-3">${this._esc(tmpState.Message)}</div>`;
	}

	_bodyHTML()
	{
		const tmpState = this.state;
		if (tmpState.Loading && !tmpState.Loaded)
		{
			return `<div class="pew-empty box has-text-grey has-text-centered"><span class="pew-spinner"></span><p class="mt-3">Loading…</p></div>`;
		}
		if (!tmpState.Loaded)
		{
			return `<div id="PEW-Message">${this._messageHTML()}</div><div class="pew-empty box has-text-grey has-text-centered"><p>Pick a project to edit.</p></div>`;
		}
		const tmpCanEdit = this.canEdit;
		const tmpLockBanner = (tmpCanEdit && this.isActive)
			? `<div class="pew-lock-banner"><span class="pew-lock-icon">🔒</span><span>This project is <strong>Active</strong> — its Schedule &amp; Financials are locked. Other fields remain editable.</span></div>`
			: '';
		const tmpReadOnlyNote = tmpCanEdit
			? ''
			: `<div class="notification is-warning is-soft py-2 px-3 mb-3">You can view this project but not change it.</div>`;
		const tmpActions = tmpCanEdit
			? `<button class="button is-link"${tmpState.Saving ? ' disabled' : ''} onclick="_Pict.providers.ProjectEditWorkspace.saveAsync(); return false;"><span>Save</span></button>`
				+ `<button class="button is-light" onclick="_Pict.providers.ProjectEditWorkspace.cancel(); return false;"><span>Cancel</span></button>`
			: `<button class="button is-light" onclick="_Pict.providers.ProjectEditWorkspace.cancel(); return false;"><span>Back to project</span></button>`;
		return `<div class="pew-head">`
			+ `<div><div class="pew-eyebrow">${tmpCanEdit ? 'Edit Project' : 'Project'}</div><h1 class="pew-title">${this._esc(tmpState.Title)}</h1></div>`
			+ `<div class="pew-head-actions">${tmpActions}</div></div>`
			+ tmpReadOnlyNote
			+ `<div id="PEW-Message">${this._messageHTML()}</div>`
			+ tmpLockBanner
			+ `<div class="pew-form">${FIELD_GROUPS.map((pGroup) => this._groupHTML(pGroup)).join('')}</div>`;
	}

	_groupHTML(pGroup)
	{
		const tmpGated = (pGroup.Name === GATED_GROUP) && this.isActive;
		const tmpHint = tmpGated ? `<span class="pew-group-hint">🔒 Locked while Active</span>` : '';
		const tmpRows = pGroup.Fields.map((pField) => this._fieldHTML(pField)).join('');
		return `<div class="pew-group"><div class="pew-group-head">${this._esc(pGroup.Name)}${tmpHint}</div><div class="pew-grid">${tmpRows}</div></div>`;
	}

	_fieldHTML(pField)
	{
		const tmpState = this.state;
		const tmpValue = tmpState.Fields[pField.Key];
		const tmpDisabled = !this.canEdit || !!(pField.Gated && this.isActive);
		const tmpError = tmpState.Errors[pField.Key] ? `<div class="pew-field-error">${this._esc(tmpState.Errors[pField.Key])}</div>` : '';
		const tmpWide = pField.Wide ? ' pew-field-wide' : '';
		return `<div class="pew-field${tmpWide}"><label class="pew-field-label">${this._esc(pField.Label)}</label>${this._controlHTML(pField, tmpValue, tmpDisabled)}${tmpError}</div>`;
	}

	_controlHTML(pField, pValue, pDisabled)
	{
		const tmpDisabledAttr = pDisabled ? ' disabled' : '';
		const tmpOnChange = `onchange="_Pict.providers.ProjectEditWorkspace.setField('${pField.Key}', this.value)"`;
		if (pField.Type === 'textarea')
		{
			return `<textarea class="textarea pew-input"${tmpDisabledAttr} ${tmpOnChange}>${this._esc(pValue)}</textarea>`;
		}
		if (pField.Type === 'entity')
		{
			// A searchable pict-section-picker mounted into this host after render() (see _mountPickers).
			return `<div id="PEW-Picker-${pField.Key}" class="pew-input pew-picker-host"></div>`;
		}
		const tmpStep = (pField.Type === 'number') ? ' step="any"' : '';
		return `<input class="input pew-input" type="${(pField.Type === 'number') ? 'number' : 'text'}"${tmpStep}${tmpDisabledAttr} value="${this._esc(pValue)}" ${tmpOnChange} />`;
	}
}

module.exports = ProjectEditWorkspaceProvider;

module.exports.default_configuration =
{
	ProviderIdentifier: 'ProjectEditWorkspace',
	AutoInitialize: true,
	AutoInitializeOrdinal: 221,
};
